import { useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { AdditiveBlending, Object3D } from 'three';
import { seededRandom } from './random.js';
import { softSpriteTexture } from './softSprite.js';

const dummy = new Object3D();
const COUNT = 340;

// Camera-attached hyperspace streaks: thin instanced boxes in a cylinder
// around the view axis, stretched along -Z by warp intensity (research §3 —
// the streak reads from motion + stretch, not geometry detail). Invisible at
// warp≈0; the group rides the camera every frame.
export function WarpStreaks({ warpRef }) {
  const groupRef = useRef();
  const meshRef = useRef();
  const glowRef = useRef();
  const { camera } = useThree();
  const glow = softSpriteTexture({
    stops: [
      [0, 'rgba(235,240,255,0.5)'],
      [0.4, 'rgba(190,205,240,0.16)'],
      [1, 'rgba(160,180,230,0)'],
    ],
  });

  const seedsArr = useMemo(() => {
    const random = seededRandom(97);
    return Array.from({ length: COUNT }, () => ({
      angle: random() * Math.PI * 2,
      radius: 1.2 + random() * 5.5,
      z: -random() * 46,
      len: 0.6 + random() * 1.4,
      speed: 18 + random() * 30,
    }));
  }, []);

  useFrame((state, dt) => {
    const warp = warpRef.current ?? 0;
    if (!groupRef.current || !meshRef.current) return;
    groupRef.current.position.copy(camera.position);
    groupRef.current.quaternion.copy(camera.quaternion);
    groupRef.current.visible = warp > 0.02;
    if (!groupRef.current.visible) return;

    for (let i = 0; i < COUNT; i++) {
      const s = seedsArr[i];
      s.z += s.speed * warp * dt;
      if (s.z > 4) s.z -= 50;
      dummy.position.set(Math.cos(s.angle) * s.radius, Math.sin(s.angle) * s.radius, s.z);
      dummy.scale.set(0.012, 0.012, s.len * (0.2 + warp * 6));
      dummy.rotation.set(0, 0, 0);
      dummy.updateMatrix();
      meshRef.current.setMatrixAt(i, dummy.matrix);
    }
    meshRef.current.instanceMatrix.needsUpdate = true;
    meshRef.current.material.opacity = 0.5 * warp;
    if (glowRef.current) glowRef.current.material.opacity = warp * 0.32; // hyperdrive-style center energy
  });

  return (
    <group ref={groupRef} visible={false}>
      <instancedMesh ref={meshRef} args={[undefined, undefined, COUNT]} frustumCulled={false}>
        <boxGeometry args={[1, 1, 1]} />
        <meshBasicMaterial color="#cfd8ee" transparent opacity={0} depthWrite={false} blending={AdditiveBlending} />
      </instancedMesh>
      <sprite ref={glowRef} position={[0, 0, -14]} scale={9}>
        <spriteMaterial map={glow} transparent opacity={0} depthWrite={false} blending={AdditiveBlending} />
      </sprite>
    </group>
  );
}
